import { useTenantMetadata } from '@globals/useTenantMetadata'
import { getMemberAllTimeStats } from '@kudos/http-client'
import { useMemberQualityStats } from '@shared/qualities/useMemberQualityStats'
import { useTranslatedField } from '@shared/utils/useTranslatedField'
import { useQuery } from '@tanstack/react-query'
import { useT } from '@transifex/react'
import { useMemo } from 'react'
import { TimelineDirection } from '../../types'
import {
  RecognitionSummaryTile,
  findTopQuality,
  getRecognitionSummaryTiles,
} from './memberRecognitionSummaryUtils'

export const MEMBER_ALL_TIME_STATS_QK = 'memberAllTimeStats'

/**
 * The summary metrics for a member's Recognition tab. Two endpoints back them:
 * the timeline's all-time counts, and the quality stats that also feed the
 * About tab's recognition profile.
 *
 * `enabled` is what keeps the narrow layout, which does not show the row, from
 * fetching counts nobody will read.
 */
const useMemberRecognitionSummary = (
  memberId: string,
  direction: TimelineDirection,
  enabled = true,
) => {
  const t = useT()
  const getTranslatedField = useTranslatedField()
  const [{ language }] = useTenantMetadata()

  const { data: stats } = useQuery({
    queryKey: [MEMBER_ALL_TIME_STATS_QK, memberId],
    queryFn: () => getMemberAllTimeStats(memberId),
    staleTime: 60 * 1000,
    enabled: enabled && !!memberId,
  })

  const { data: qualityStats } = useMemberQualityStats(memberId, enabled)

  // Kept referentially stable so switching between Received and Given, or a
  // re-render from the feed below, does not hand the tiles a new array.
  const tiles: RecognitionSummaryTile[] = useMemo(
    () =>
      getRecognitionSummaryTiles(
        direction,
        stats,
        findTopQuality(qualityStats ?? [], getTranslatedField),
        t,
      ),
    // `getTranslatedField` is a fresh closure on every render; the tenant
    // language is what actually changes what it returns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [direction, stats, qualityStats, language, t],
  )

  return { tiles }
}

export default useMemberRecognitionSummary
